/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : Notifications.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import notificationsAction from "../../redux/actions/notification";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../redux/actions";
import appTheme from "../../assets/AppTheme/appTheme";

const Notifications = ({ onClose, notifications }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [recentNotificationDetails, setRecentNotificationDetails] = useState(
    []
  );

  const deleteFromQueue = async ({ messageIds, dbIds }) => {
    const apiDetails = notificationsAction.delete_notification_from_queue;
    await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: { messageIds, dbIds },
          show_toast: false,
        })
      )
    );
  };

  const handleClose = () => {
    const messageIds = [];
    const dbIds = [];
    for (const notification of notifications || []) {
      if (notification?.que_id) {
        messageIds.push(notification.id);
      }
      if (notification?.db_id) {
        dbIds.push(notification.db_id);
      }
    }
    if (messageIds.length || dbIds.length) {
      deleteFromQueue({
        messageIds,
        dbIds,
      });
    }

    if (onClose) {
      onClose();
    }
  };

  const handleViewAll = () => {
    handleClose();
    navigate("/dashboard/notification");
  };

  useEffect(() => {
    if (notifications?.length) {
      setRecentNotificationDetails(notifications);
    } else {
      setRecentNotificationDetails([]);
    }
  }, [notifications]);

  return (
    <Paper
      sx={{
        position: "absolute",
        top: 50,
        right: 20,
        width: 300,
        zIndex: 1300,
        padding: 1,
        boxShadow: 15,
        border: "1px solid black",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 1,
          height: 40,
        }}
      >
        <Typography>Notifications</Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ maxHeight: 250, overflowY: "auto" }}>
        {(recentNotificationDetails || []).map((notification, index) => (
          <Box key={notification.id || notification.db_id || index} sx={{ padding: 0.5, textAlign: "left" }}>
            <Paper
              sx={{ border: "1px solid black", padding: "5px", boxShadow: 5 }}
            >
              <Button onClick={handleViewAll}>
                <Typography sx={{ fontSize: 12, textAlign: "left" }}>
                  <strong>{notification.header}</strong>
                </Typography>
              </Button>
              <Typography sx={{ fontSize: 10 }}>
                {notification.message}
              </Typography>
            </Paper>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          paddingTop: 1,
          display: "flex",
          justifyContent: "flex-end",
          height: 40,
        }}
      >
        <Button
          variant="contained"
          onClick={handleViewAll}
          sx={{ ...appTheme.commonBtnStyle, fontSize: "10px" }}
        >
          View All
        </Button>
      </Box>
    </Paper>
  );
};

Notifications.propTypes = {
  onClose: PropTypes.func,
  notifications: PropTypes.array,
};

export default Notifications;
